import { Code2, Copy, Download, Hash, LinkIcon, Printer, X } from 'lucide-react';
import {
  type ReactElement,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useInterfaceLanguage } from '@/lib/interface-language';
import { Button } from '../Button';
import { IconButton } from '../IconButton';
import { Notice } from '../Notice';
import { TextField } from '../TextField';
import { MobileParityNotice } from '../board/MobileParityNotice';

export interface SharePanelItem {
  readonly kind: 'board' | 'card';
  readonly title: string;
  readonly path: string;
  /**
   * A card's `KEY-NNNN` reference (`docs/notes/card-keys.md`), as the server
   * scoped it to this viewer. Present and non-null only for a card the viewer
   * may address by reference; the board arm, an inbox card and a public-arm
   * viewer pass nothing (or `null`) and get no "Copy reference" button.
   */
  readonly reference?: string | null | undefined;
  readonly visibility: 'private' | 'workspace' | 'public' | 'enterprise';
  readonly embedPath?: string | undefined;
  readonly exportJson?: unknown;
  readonly exportAvailable?: boolean | undefined;
  readonly exportBusy?: boolean | undefined;
  readonly exportDisabledReason?: string | undefined;
  readonly printView?: ReactNode | undefined;
  readonly printBusy?: boolean | undefined;
  readonly printDisabledReason?: string | undefined;
}

export interface SharePanelProps {
  readonly item: SharePanelItem;
  readonly origin?: string | undefined;
  readonly onClose?: (() => void) | undefined;
  readonly mobileDeclaration?: SharePanelMobileDeclaration | undefined;
}

export interface SharePanelMobileDeclaration {
  readonly title: string;
  readonly body: string;
  readonly href: string;
}

export function canonicalShareUrl(item: SharePanelItem, origin: string): string {
  return new URL(item.path, normaliseOrigin(origin)).toString();
}

export function markdownShareLink(item: SharePanelItem, origin: string): string {
  return `[${escapeMarkdownLabel(item.title)}](${canonicalShareUrl(item, origin)})`;
}

export function embedSnippet(item: SharePanelItem, origin: string): string {
  const src = escapeHtmlAttribute(
    item.embedPath === undefined
      ? canonicalShareUrl(item, origin)
      : new URL(item.embedPath, normaliseOrigin(origin)).toString(),
  );
  const title = escapeHtmlAttribute(item.title);

  return `<iframe src="${src}" title="${title}" loading="lazy" sandbox="allow-scripts" referrerpolicy="no-referrer"></iframe>`;
}

/**
 * FE-80.1 — the eight `(kind × visibility)` branches, as a total map from the
 * pair to a catalogue key.
 *
 * ⚠ This replaces `visibilityMessage`, which returned the sentence itself. The
 * key is the honest return value once the sentences live in the catalogue: it
 * keeps the branch table pure and testable without a provider, and — more to
 * the point — it makes all eight branches assertable as eight. Only two of the
 * eight were ever covered, and a swap between the card and board arms of the
 * same visibility renders a real, grammatical sentence with nothing red.
 *
 * The card arm is not the board arm with a word changed: a card link needs
 * board access *in addition to* the workspace or enterprise membership, which
 * is why those two arms say "still need" where the board arms say "with".
 */
export function visibilityMessageKey(item: SharePanelItem): string {
  if (item.visibility === 'public') {
    return item.kind === 'card'
      ? 'share.visibility.cardPublic'
      : 'share.visibility.boardPublic';
  }

  if (item.kind === 'card') {
    if (item.visibility === 'private') return 'share.visibility.cardPrivate';
    if (item.visibility === 'enterprise') return 'share.visibility.cardEnterprise';
    return 'share.visibility.cardWorkspace';
  }

  if (item.visibility === 'private') return 'share.visibility.boardPrivate';
  if (item.visibility === 'enterprise') return 'share.visibility.boardEnterprise';
  return 'share.visibility.boardWorkspace';
}

/**
 * FE-80.1 — which value the panel just put on the clipboard, as a TOKEN rather
 * than as the noun that names it.
 *
 * ⚠ The panel used to carry `'Link'` / `'Markdown link'` / `'Embed snippet'`
 * through this state and build `${source} copied.` and `${source} ready for
 * manual copy` around it. Routing that shape would have handed a translator two
 * sentence frames and three bare nouns to slot into them — English word order
 * frozen, and the noun with no case, gender or agreement to inflect. Six whole
 * sentences cost one key more than five fragments and are translatable.
 */
type CopySource = 'link' | 'reference' | 'markdown' | 'embed';

const COPY_KEYS: Record<
  CopySource,
  { readonly copied: string; readonly manualTitle: string }
> = {
  link: { copied: 'share.copiedLink', manualTitle: 'share.manualCopyLink' },
  reference: {
    copied: 'share.copiedReference',
    manualTitle: 'share.manualCopyReference',
  },
  markdown: { copied: 'share.copiedMarkdown', manualTitle: 'share.manualCopyMarkdown' },
  embed: { copied: 'share.copiedEmbed', manualTitle: 'share.manualCopyEmbed' },
};

interface ManualCopyState {
  readonly value: string;
  readonly source: CopySource;
  readonly sequence: number;
}

/**
 * FE-80.1 — the share panel, mounted twice with different `kind`: as a card by
 * `CardBack.tsx:1494` and as a board by `BoardMenuPanel.tsx:3116`. Every string
 * this file authored now comes from the catalogue. The item TITLE, the share
 * URL, the Markdown link and the embed snippet stay literal — they are board
 * data and a generated artefact, and a translator must never see either.
 *
 * ⚠ One copy defect this file still has, reported in the chunk's return rather
 * than fixed here because fixing it moves bytes an e2e spec pins: both
 * `disabledReason` defaults say "Card …" whatever `kind` is. Unreachable from
 * the board arm today, because no board caller supplies `exportAvailable` or
 * `printBusy`.
 */
export function SharePanel({
  item,
  origin,
  onClose,
  mobileDeclaration,
}: SharePanelProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const headingId = useId();
  // ⚠ The SOURCE, not the sentence. Storing the rendered string here would
  // freeze it at the moment of the copy, so a language change with the notice
  // on screen would leave the previous language sitting in it.
  const [copySource, setCopySource] = useState<CopySource | null>(null);
  const [manualCopy, setManualCopy] = useState<ManualCopyState | null>(null);
  const [printOpen, setPrintOpen] = useState(false);
  const manualCopyRef = useRef<HTMLInputElement | null>(null);
  const manualCopySequence = useRef(0);

  const panelOrigin = origin ?? browserOrigin();
  const shareUrl = useMemo(
    () => canonicalShareUrl(item, panelOrigin),
    [item, panelOrigin],
  );
  const markdownLink = useMemo(
    () => markdownShareLink(item, panelOrigin),
    [item, panelOrigin],
  );
  const iframe = useMemo(() => embedSnippet(item, panelOrigin), [item, panelOrigin]);
  const canExportJson = item.exportAvailable === true || item.exportJson !== undefined;
  const exportDisabled = canExportJson && item.exportJson === undefined;
  const exportBusyProps = item.exportBusy === undefined ? {} : { busy: item.exportBusy };
  const exportDisabledReason = exportDisabled
    ? (item.exportDisabledReason ?? t('share.exportLoading'))
    : undefined;
  const exportDisabledReasonProps =
    exportDisabledReason === undefined ? {} : { disabledReason: exportDisabledReason };
  const printAvailable = item.printView !== undefined || item.printBusy === true;
  const printDisabled = item.printView === undefined;
  const printBusyProps = item.printBusy === undefined ? {} : { busy: item.printBusy };
  const printDisabledReason = printDisabled
    ? (item.printDisabledReason ?? t('share.printLoading'))
    : undefined;
  const printDisabledReasonProps =
    printDisabledReason === undefined ? {} : { disabledReason: printDisabledReason };

  useEffect(() => {
    if (manualCopy === null) return;
    const field = manualCopyRef.current;
    field?.focus();
    field?.select();
  }, [manualCopy]);

  async function copyValue(value: string, source: CopySource): Promise<void> {
    const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard;

    if (clipboard?.writeText === undefined) {
      showManualCopy(value, source);
      return;
    }

    try {
      await clipboard.writeText(value);
      setManualCopy(null);
      setCopySource(source);
    } catch {
      showManualCopy(value, source);
    }
  }

  function showManualCopy(value: string, source: CopySource): void {
    manualCopySequence.current += 1;
    setCopySource(null);
    setManualCopy({ value, source, sequence: manualCopySequence.current });
  }

  function exportCardJson(): void {
    if (item.exportJson === undefined || typeof document === 'undefined') return;

    const blob = new Blob([`${JSON.stringify(item.exportJson, null, 2)}\n`], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${downloadBaseName(item.title)}.json`;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  function openPrintView(): void {
    if (item.printView === undefined) return;
    setPrintOpen(true);
  }

  return (
    <section
      aria-labelledby={headingId}
      className="flex w-full max-w-md flex-col gap-4 rounded-card border border-border-strong bg-surface p-4 text-ink shadow-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <h2 id={headingId} className="break-words text-base font-semibold text-ink">
            {t('share.heading', { title: item.title })}
          </h2>
          <p className="text-sm text-ink-muted">{t(visibilityMessageKey(item))}</p>
        </div>
        {onClose !== undefined ? (
          <IconButton type="button" label={t('share.close')} size="sm" onClick={onClose}>
            <X aria-hidden="true" className="size-4" />
          </IconButton>
        ) : null}
      </div>

      {mobileDeclaration === undefined ? null : (
        <MobileParityNotice
          kind="card-share"
          title={mobileDeclaration.title}
          body={mobileDeclaration.body}
          href={mobileDeclaration.href}
        />
      )}

      <TextField label={t('share.urlLabel')} readOnly value={shareUrl} />

      <div className="grid grid-cols-[repeat(auto-fit,minmax(11rem,1fr))] gap-2">
        <Button
          type="button"
          iconStart={<LinkIcon aria-hidden="true" className="size-4" />}
          onClick={() => {
            void copyValue(shareUrl, 'link');
          }}
        >
          {t('share.copyLink')}
        </Button>
        {item.reference === undefined || item.reference === null ? null : (
          <Button
            type="button"
            iconStart={<Hash aria-hidden="true" className="size-4" />}
            onClick={() => {
              // The PLAIN reference (`MG-0001`) — what people paste — never a URL.
              if (item.reference) void copyValue(item.reference, 'reference');
            }}
          >
            {t('share.copyReference')}
          </Button>
        )}
        <Button
          type="button"
          iconStart={<Copy aria-hidden="true" className="size-4" />}
          onClick={() => {
            void copyValue(markdownLink, 'markdown');
          }}
        >
          {t('share.copyMarkdown')}
        </Button>
        <Button
          type="button"
          iconStart={<Code2 aria-hidden="true" className="size-4" />}
          onClick={() => {
            void copyValue(iframe, 'embed');
          }}
        >
          {t('share.copyEmbed')}
        </Button>
        {canExportJson ? (
          <Button
            type="button"
            iconStart={<Download aria-hidden="true" className="size-4" />}
            disabled={exportDisabled}
            busyLabel={t('share.exportBusy')}
            onClick={exportCardJson}
            {...exportBusyProps}
            {...exportDisabledReasonProps}
          >
            {t('share.exportJson')}
          </Button>
        ) : null}
        {printAvailable ? (
          <Button
            type="button"
            iconStart={<Printer aria-hidden="true" className="size-4" />}
            disabled={printDisabled}
            busyLabel={t('share.printBusy')}
            onClick={openPrintView}
            {...printBusyProps}
            {...printDisabledReasonProps}
          >
            {t('share.print')}
          </Button>
        ) : null}
      </div>

      {copySource !== null ? (
        <Notice tone="info" body={t(COPY_KEYS[copySource].copied)} />
      ) : null}

      {manualCopy !== null ? (
        <Notice
          tone="info"
          title={t(COPY_KEYS[manualCopy.source].manualTitle)}
          body={
            <TextField
              key={manualCopy.sequence}
              ref={manualCopyRef}
              label={t('share.manualCopyLabel')}
              readOnly
              value={manualCopy.value}
            />
          }
        />
      ) : null}

      {printOpen && item.printView !== undefined ? (
        <section
          aria-label={t('share.printPreview', { title: item.title })}
          className="rounded-card border border-border-strong bg-surface-raised p-4"
        >
          <div className="mb-3 flex items-center justify-end">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => {
                window.print();
              }}
            >
              {t('share.sendToPrinter')}
            </Button>
          </div>
          {item.printView}
        </section>
      ) : null}
    </section>
  );
}

function browserOrigin(): string {
  if (typeof window === 'undefined') return 'http://localhost';
  return window.location.origin;
}

function normaliseOrigin(origin: string): string {
  return `${new URL(origin).origin}/`;
}

function escapeMarkdownLabel(value: string): string {
  return value
    .replace(/\s+/g, ' ')
    .replace(/([\\[\]])/g, '\\$1')
    .trim();
}

function escapeHtmlAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function downloadBaseName(title: string): string {
  const cleaned = title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return cleaned.length === 0 ? 'card' : cleaned;
}
